import { GetServerSideProps } from 'next'
import { Post, BlogPostItemFields } from 'features/Blog/screens/Post'
import Head from 'next/head'
import { createContentfulClient } from 'utils/createContentfulClient'
import { BlogPostMetadata, BlogPostSystemFields } from 'features/Blog/screens/Post/Post'
import Script from 'next/script'

type BlogPost = {
  items: {
    fields: BlogPostItemFields
    metadata: BlogPostMetadata
    sys: BlogPostSystemFields
  }[]
}

const BlogPost = ({ post, related }: { post: BlogPost; related: BlogPost }) => {
  const item = post.items[0]

  function grabBlogContent(contentArray: any) {
    let textContent = ''

    contentArray.forEach((node: any) => {
      if (node.nodeType === 'text') {
        textContent += node.value + ' '
      } else if (node.content && Array.isArray(node.content)) {
        textContent += grabBlogContent(node.content)
      }
    })

    return textContent.trim()
  }

  // prettier-ignore
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://wefeedraw.com/blog/${item.fields.postUrl}`
    },
    headline: `${item.fields.postTitle}`,
    description: `${item.fields.postSummary}`,
    image: `https:${item.fields.headerImage?.fields?.file.url}`,
    author: {
      '@type': 'Organization',
      name: 'We Feed Raw',
      logo: {
        '@type': 'ImageObject',
        url: 'https://wefeedraw.com/assets/isotype-background-brown.svg'
      }
    },
    publisher: {
      '@type': 'Organization',
      name: 'We Feed Raw',
      logo: {
        '@type': 'ImageObject',
        url: 'https://wefeedraw.com/assets/isotype-background-brown.svg'
      }
    },
    datePublished: `${item.sys.createdAt}`,
    dateModified: `${item.sys.updatedAt}`,
    articleBody: grabBlogContent(item.fields.postContent1.content)
  };

  const jsonLdString = JSON.stringify(schema)

  return (
    <div>
      <Head>
        <title>{item.fields.pageTitle}</title>
        <meta name="title" content={item.fields.metaTitle} />
        <meta name="description" content={item.fields.metaDescription} />
        <meta property="og:image" content={`https:${item.fields.headerImage?.fields?.file.url}`} />
        <link rel="canonical" href={`https://wefeedraw.com/blog/${item.fields.postUrl}`} />
        {/* {item.fields.schema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(item.fields.schema) }}
          />
        )} */}
      </Head>

      <main>
      <Script
        id="schema-script"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLdString }}
      />
        <Post post={item} relatedPosts={related} />
      </main>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = createContentfulClient()

  const slug = ctx.query.id

  const post = await client.getEntries({
    content_type: 'blogPost',
    'fields.postUrl': slug,
  })

  const related = await client.getEntries({
    content_type: 'blogPost',
    'fields.postUrl[ne]': slug,
    limit: 3,
    skip: 0,
  })

  if (post.items.length < 1) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      post,
      related,
    },
  }
}

export default BlogPost
